import { useMemo } from 'react';
import type { MarkerProperties } from '@alltrails/types/CustomRoute';
import { useAppSelector } from '../../../redux';
import { selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import type { LatLng } from '../../../types/Geo';
import { getAdjacentEndpointToLink } from '../../../utils/customRoutes/segmentLinking';
import useCanLinkSegments from '../useCanLinkSegments';
import type { SegmentEnd } from './useAdjacentEndpointLinking';
import { getMarkerEnd } from './useDragMarker';
import { getExtendedEnd } from './useRouteToPoint';

export type LinkableEndpoint = {
  /** The editing segment's end that would link: its start to the previous segment, its end to the next */
  end: SegmentEnd;
  endpoint: LatLng;
};

/**
 * The neighboring segment's endpoint that the next placed point, or the dragged start or end handle, links to when placed near it.
 * @param dragMarker the handle being dragged, if any
 */
const useLinkableEndpoint = (dragMarker?: MarkerProperties): LinkableEndpoint | undefined => {
  const canLinkSegments = useCanLinkSegments();
  const { activeSegment, activeSegmentIndex, isInCutMode, segments, selectedHandleIndex } = useAppSelector(state => ({
    activeSegment: selectCustomRouteActiveSegment(state),
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    isInCutMode: state.customRoute.cutData !== undefined,
    segments: state.customRoute.route.segments,
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));

  return useMemo(() => {
    if (!canLinkSegments || isInCutMode) {
      return undefined;
    }
    // A segment not added yet is one past the last
    const editingSegmentIndex = activeSegmentIndex ?? segments.length;
    const isDraggingEditingSegment = dragMarker && dragMarker.segmentIndex === editingSegmentIndex;
    const end = dragMarker ? (isDraggingEditingSegment ? getMarkerEnd(dragMarker) : undefined) : getExtendedEnd(activeSegment, selectedHandleIndex);
    const endpoint = end ? getAdjacentEndpointToLink(segments, editingSegmentIndex, end) : undefined;
    return end && endpoint ? { end, endpoint } : undefined;
  }, [activeSegment, activeSegmentIndex, canLinkSegments, dragMarker, isInCutMode, segments, selectedHandleIndex]);
};

export default useLinkableEndpoint;
